import { where } from 'firebase/firestore'
import { flatten, isEqual, uniq } from 'lodash-es'
import { useEffect, useState } from 'react'
import {
  helperLeadProperties,
  HelperLeadProperty,
  ILayout,
  ITrait,
  OmitDBProps,
  primaryLeadProperties,
  PrimaryLeadProperty,
  TemplateRoute,
  TemplateRouteNode,
  TemplateRouting,
  TemplateValueRoute,
} from 'types'
import { getDocById, useDocs, usePrefCurrentWorkspace } from 'utils'
import { useTemplate } from '../components'

export const makeEmptyTrait = (workspaceId: string): OmitDBProps<ITrait> => ({
  isMulti: false,
  title: { en: '' },
  values: [],
  workspaceId,
})

export function hideTraitValues(leadField?: string): boolean {
  const hideValueFields = ['followUp', 'followUpGroup', 'followUpUserId', 'temp']
  return hideValueFields.includes(leadField || '')
}

const hiddenPrimaryLeadProperties: Set<PrimaryLeadProperty> = new Set([
  'fullOriginalAddress',
  'email',
  'lat',
  'lng',
  'firstName',
  'lastName',
])

const hiddenHelperLeadProperties: Set<HelperLeadProperty> = new Set([
  'apiDataStatus',
  'badge',
  'crmSync',
  'consents',
  'geoCodingStatus',
  'optIn',
  'smartPropertyFailure',
  'processingStatus',
])

export const leadTraitProperties = [
  ...primaryLeadProperties.filter(p => !hiddenPrimaryLeadProperties.has(p)),
  ...helperLeadProperties.filter(p => !hiddenHelperLeadProperties.has(p)),
]

export function useTraits() {
  const [workspaceId] = usePrefCurrentWorkspace()
  const [traits, status] = useDocs('traits2', [where('workspaceId', '==', workspaceId)])
  const sorted = [...traits].sort((a, b) => a.title.en.localeCompare(b.title.en))

  return { traits: sorted, status }
}

export type FullTrait = ITrait & { hasEdits: boolean }

export function useFullTraitMap() {
  const { template } = useTemplate()
  const [templateTraits, setTemplateTraits] = useState<Record<number, FullTrait>>()

  useEffect(() => {
    const traitMap = template?.traitMap
    if (!template) return

    async function load() {
      if (!traitMap) return

      const entries = await Promise.all(
        Object.entries(traitMap).map(async ([key, trait]) => {
          const { id, values, leadField } = trait
          const traitKey = Number(key)
          if (!id) {
            // Handle special lead fields that need auto-generated values for routing
            if (hideTraitValues(leadField) && (!values || values.length === 0)) {
              let autoValues
              if (leadField === 'followUp') {
                autoValues = [
                  { id: `${traitKey}-true`, title: { en: 'Yes' } },
                  { id: `${traitKey}-false`, title: { en: 'No' } },
                ]
              } else if (leadField === 'temp') {
                autoValues = [
                  { id: `${traitKey}-hot`, title: { en: 'Hot' } },
                  { id: `${traitKey}-warm`, title: { en: 'Warm' } },
                  { id: `${traitKey}-cold`, title: { en: 'Cold' } },
                ]
              }
              if (autoValues) {
                return [traitKey, { ...trait, values: autoValues }] as const
              }
            }
            return [traitKey, trait] as const
          }

          const traitDocData = await getDocById('traits2', id) // change this once restructure is complete
          const traitDocValues = traitDocData?.values ?? []

          let completeValues = values
            ? values.map(value => {
                const docValue = traitDocValues.find(v => v.id === value.id)
                return { ...docValue, ...value }
              })
            : traitDocValues

          // Handle special lead fields for shared traits
          const effectiveLeadField = trait.leadField ?? traitDocData?.leadField
          if (
            hideTraitValues(effectiveLeadField) &&
            (!completeValues || completeValues.length === 0)
          ) {
            if (effectiveLeadField === 'followUp') {
              completeValues = [
                { id: `${traitKey}-true`, title: { en: 'Yes' } },
                { id: `${traitKey}-false`, title: { en: 'No' } },
              ]
            } else if (effectiveLeadField === 'temp') {
              completeValues = [
                { id: `${traitKey}-hot`, title: { en: 'Hot' } },
                { id: `${traitKey}-warm`, title: { en: 'Warm' } },
                { id: `${traitKey}-cold`, title: { en: 'Cold' } },
              ]
            }
          }

          const merged = { ...traitDocData, ...trait, values: completeValues } as FullTrait
          return [traitKey, { ...merged, hasEdits: !isEqual(traitDocData, merged) }] as const
        })
      )
      setTemplateTraits(Object.fromEntries(entries) as Record<number, FullTrait>)
    }

    load()
  }, [template])

  return templateTraits
}

export interface TemplateRouteNodeWithConnections extends TemplateRouteNode {
  connections: ConnectionWithHighlight[]
}

export interface ConnectionWithHighlight {
  value: TemplateRouteConnection
  highlight: boolean
}

/** Array of trait value ids or 'all' */
export type TemplateRouteConnection = string[] | 'all'

function isEmptyTraitDisplayConnection(connection: TemplateRouteConnection): boolean {
  return connection !== 'all' && Array.isArray(connection) && connection.length === 0
}

export function getNodesWithConnections(
  routing: TemplateRouting,
  traitMap: Record<number, ITrait>,
  highlightRoutes: TemplateRoute[]
) {
  let nodesWithConnections: TemplateRouteNodeWithConnections[] = []

  const routes = getTemplateRoutes(routing)
  for (const route of routes) {
    const highlight = highlightRoutes.some(r => isEqual(r, route))
    const routings = getRoutings(routing, route)
    for (let i = 0; i < routings.length; i++) {
      const routing = routings[i]
      const nextRouting = routings[i + 1]
      const nextNode = nextRouting && { key: nextRouting.key, type: nextRouting.type }
      const connection = getConnectionThroughRouting(traitMap, routing, nextNode)

      if (routing.type === 'trait' && isEmptyTraitDisplayConnection(connection)) continue

      const { key, type } = routing
      const node = nodesWithConnections.find(n => n.key === key && n.type === type)
      const connectionWithHighlight = { value: connection, highlight }
      if (node) {
        const existingConnection = node.connections.find(c => isEqual(c.value, connection))
        if (existingConnection) {
          existingConnection.highlight = existingConnection.highlight || highlight
        } else {
          node.connections.push(connectionWithHighlight)
        }
      } else {
        nodesWithConnections.push({ type, key, connections: [connectionWithHighlight] })
      }
    }
  }

  return nodesWithConnections
}

function getConnectionThroughRouting(
  traitMap: Record<number, ITrait>,
  routing: TemplateRouting,
  nextNode?: TemplateRouteNode
): TemplateRouteConnection {
  const { key, type, valueRoutes = [], default: defaultRoute } = routing
  if (type === 'layout') return []

  const trait = traitMap[key]
  if (!nextNode || !trait) return []

  for (const v of valueRoutes) {
    const valueNode = { key: v.to.key, type: v.to.type }
    if (isEqual(valueNode, nextNode)) return v.fromIds
  }

  const defaultNode = { key: defaultRoute?.key, type: defaultRoute?.type }
  if (!isEqual(defaultNode, nextNode)) return []

  if (valueRoutes.length) {
    const usedValueIds = uniq(flatten(valueRoutes.map(v => v.fromIds)))
    const otherValueIds = trait.values.filter(v => !usedValueIds.includes(v.id)).map(v => v.id)
    return otherValueIds
  }

  return 'all'
}

export function matchStringArrays(arr1: string[], arr2: string[]) {
  const _arr1 = uniq(arr1).sort()
  const _arr2 = uniq(arr2).sort()
  return isEqual(_arr1, _arr2)
}

export interface ValueAssignment {
  type: 'default' | 'unused' | 'value-route'
  valueRouteIndex?: number
}

export function findValueAssignment(routing: TemplateRouting, valueId: string): ValueAssignment {
  const valueRouteIndex = routing.valueRoutes?.findIndex(vr => vr.fromIds.includes(valueId)) ?? -1
  if (valueRouteIndex > -1) return { type: 'value-route', valueRouteIndex }

  const used = new Set((routing.valueRoutes ?? []).flatMap(vr => vr.fromIds))
  if (!used.has(valueId)) {
    return routing.default ? { type: 'default' } : { type: 'unused' }
  }

  return { type: 'unused' }
}

export function getDefaultValueIds(routing: TemplateRouting, trait: ITrait): string[] {
  const used = new Set((routing.valueRoutes ?? []).flatMap(vr => vr.fromIds))
  return trait.values.map(value => value.id).filter(id => !used.has(id))
}

export function getUngroupedValueIds(routing: TemplateRouting, trait: ITrait): string[] {
  const used = new Set((routing.valueRoutes ?? []).flatMap(vr => vr.fromIds))
  if (routing.default) return []
  return trait.values.map(value => value.id).filter(id => !used.has(id))
}

export function removeValueFromTraitRouting(
  routing: TemplateRouting,
  trait: ITrait,
  valueId: string
): TemplateRouting {
  const assignment = findValueAssignment(routing, valueId)
  const valueRoutes = [...(routing.valueRoutes ?? [])]

  if (assignment.type === 'value-route' && assignment.valueRouteIndex != null) {
    const valueRoute = valueRoutes[assignment.valueRouteIndex]
    const nextFromIds = valueRoute.fromIds.filter(id => id !== valueId)
    if (nextFromIds.length > 0) {
      valueRoutes[assignment.valueRouteIndex] = { ...valueRoute, fromIds: nextFromIds }
    } else {
      valueRoutes.splice(assignment.valueRouteIndex, 1)
    }

    const next: TemplateRouting = { ...routing }
    if (valueRoutes.length > 0) next.valueRoutes = valueRoutes
    else delete next.valueRoutes
    return next
  }

  if (assignment.type === 'default' && routing.default) {
    const defaultValueIds = getDefaultValueIds(routing, trait).filter(id => id !== valueId)
    const next: TemplateRouting = { ...routing }

    if (defaultValueIds.length > 0) {
      next.valueRoutes = [...valueRoutes, { fromIds: defaultValueIds, to: routing.default }]
    } else if (valueRoutes.length > 0) {
      next.valueRoutes = valueRoutes
    }

    delete next.default
    return next
  }

  return routing
}

export function getTargetGroupIdsFromRoute(
  routing: TemplateRouting,
  trait: ITrait,
  routeStep: TemplateRoute[number] | undefined,
  childRouting?: TemplateRouting
): string[] {
  if (routeStep === 'default') {
    if (routing.default) return getDefaultValueIds(routing, trait)

    const matchingValueRoute = resolveValueRouteForChild(routing, childRouting)
    return matchingValueRoute?.fromIds ?? []
  }

  if (typeof routeStep === 'number') {
    const valueRoute = routing.valueRoutes?.[routeStep]
    if (valueRoute) return valueRoute.fromIds

    const matchingValueRoute = resolveValueRouteForChild(routing, childRouting)
    return matchingValueRoute?.fromIds ?? []
  }

  return []
}

function resolveValueRouteForChild(
  routing: TemplateRouting,
  childRouting?: TemplateRouting
): TemplateValueRoute | undefined {
  if (!childRouting) return undefined

  const matchingValueRoutes =
    routing.valueRoutes?.filter(
      valueRoute =>
        valueRoute.to.key === childRouting.key && valueRoute.to.type === childRouting.type
    ) ?? []

  if (matchingValueRoutes.length === 0) return undefined
  if (matchingValueRoutes.length === 1) return matchingValueRoutes[0]

  return matchingValueRoutes.sort((a, b) => b.fromIds.length - a.fromIds.length)[0]
}

function findValueRouteIndexForGroup(
  valueRoutes: TemplateRouting['valueRoutes'],
  groupIds: string[]
): number {
  if (!groupIds.length || !valueRoutes?.length) return -1
  return valueRoutes.findIndex(valueRoute => groupIds.every(id => valueRoute.fromIds.includes(id)))
}

export function moveValueToSelectedGroup(
  routing: TemplateRouting,
  trait: ITrait,
  valueId: string,
  targetGroupIds: string[]
): TemplateRouting {
  if (targetGroupIds.length === 0) {
    return removeValueFromTraitRouting(routing, trait, valueId)
  }

  const targetValueRouteIndex = findValueRouteIndexForGroup(routing.valueRoutes, targetGroupIds)
  const targetIsDefault =
    routing.default != null &&
    targetGroupIds.every(id => getDefaultValueIds(routing, trait).includes(id))

  let targetFromIdsBase: string[]
  let targetSubtree: TemplateRouting | undefined

  if (targetValueRouteIndex > -1) {
    targetFromIdsBase = routing.valueRoutes![targetValueRouteIndex].fromIds
    targetSubtree = routing.valueRoutes![targetValueRouteIndex].to
  } else if (targetIsDefault) {
    targetFromIdsBase = getDefaultValueIds(routing, trait)
    targetSubtree = routing.default
  } else {
    return removeValueFromTraitRouting(routing, trait, valueId)
  }

  const targetFromIds = uniq([...targetFromIdsBase, valueId])
  const next = removeValueFromTraitRouting(routing, trait, valueId)
  const valueRoutes = [...(next.valueRoutes ?? [])]

  const mergeIndex = valueRoutes.findIndex(
    valueRoute =>
      targetFromIdsBase.every(id => valueRoute.fromIds.includes(id)) &&
      valueRoute.to.key === targetSubtree!.key &&
      valueRoute.to.type === targetSubtree!.type
  )

  if (mergeIndex > -1) {
    valueRoutes[mergeIndex] = { ...valueRoutes[mergeIndex], fromIds: targetFromIds }
  } else {
    valueRoutes.push({ fromIds: targetFromIds, to: targetSubtree! })
  }

  const result: TemplateRouting = { ...next, valueRoutes }
  if (targetIsDefault) delete result.default
  return result
}

export function updateRouting(
  totalRouting: TemplateRouting | undefined,
  route: TemplateRoute,
  finalRouting: TemplateRouting,
  traitMap?: Record<number, ITrait>
): TemplateRouting | undefined {
  if (!totalRouting) return undefined

  function updater(routing: TemplateRouting, route: TemplateRoute): TemplateRouting {
    if (route.length === 0) return finalRouting

    const [first, ...rest] = route
    if (first === 'default') {
      const updated = updater(routing.default!, rest)
      return { ...routing, default: updated }
    } else {
      const updated = updater(routing.valueRoutes?.[first]?.to!, rest)
      const valueRoutes = routing.valueRoutes!.map((vr, i) =>
        i === first ? { ...vr, to: updated } : vr
      )
      return { ...routing, valueRoutes }
    }
  }

  const updated = updater(totalRouting, route)
  return traitMap ? optimizeRoutingTree(updated, traitMap) : updated
}

/** Attach a child under the routing at route for the given value ids. Returns optimized routing and route path to the child. */
export function attachRoutingChild(
  totalRouting: TemplateRouting,
  route: TemplateRoute,
  fromIds: string[],
  child: TemplateRouteNode,
  traitMap?: Record<number, ITrait>
): { routing: TemplateRouting; route: TemplateRoute } | undefined {
  const parentRouting = getRouting(totalRouting, route)
  const valueRoutes = [...(parentRouting.valueRoutes ?? [])]
  const nextValueRoute = { fromIds, to: child }

  const existingIndex = valueRoutes.findIndex(vr => matchStringArrays(vr.fromIds, fromIds))
  if (existingIndex > -1) {
    valueRoutes[existingIndex] = nextValueRoute
  } else {
    valueRoutes.push(nextValueRoute)
  }

  const routing = updateRouting(totalRouting, route, { ...parentRouting, valueRoutes }, traitMap)
  if (!routing) return undefined

  const routeStep = resolveRouteStepForChild(getRouting(routing, route), child)
  if (routeStep === undefined) return undefined

  return { routing, route: [...route, routeStep] }
}

/** Recursively optimize trait routing, promoting the largest branch to default when all values are grouped. */
export function optimizeRoutingTree(
  routing: TemplateRouting,
  traitMap: Record<number, ITrait>
): TemplateRouting {
  const defaultOptimized = routing.default
    ? optimizeRoutingTree(routing.default, traitMap)
    : undefined
  const valueRoutesBase = routing.valueRoutes?.map(vr => ({
    ...vr,
    to: optimizeRoutingTree(vr.to, traitMap),
  }))

  return applyTraitNodeRoutingOptimizations(
    routing,
    traitMap,
    defaultOptimized,
    valueRoutesBase?.length ? valueRoutesBase : undefined
  )
}

function applyTraitNodeRoutingOptimizations(
  routing: TemplateRouting,
  traitMap: Record<number, ITrait>,
  defaultOptimized: TemplateRouting | undefined,
  valueRoutesBase: TemplateRouting['valueRoutes']
): TemplateRouting {
  if (routing.type !== 'trait') {
    return {
      ...routing,
      default: defaultOptimized,
      valueRoutes: valueRoutesBase?.length ? valueRoutesBase : undefined,
    }
  }

  const trait = traitMap[routing.key]
  const allValueIds =
    trait?.values?.map(value => value.id).filter((id): id is string => Boolean(id)) ?? []

  let next: TemplateRouting = {
    ...routing,
    default: defaultOptimized,
    valueRoutes: valueRoutesBase?.length ? valueRoutesBase : undefined,
  }

  if (allValueIds.length === 0 || !trait) return next

  if (getUngroupedValueIds(next, trait).length > 0) return next

  const rebalanced = rebalanceTraitDefaultVersusValueRoutes(next, allValueIds)
  if (rebalanced) return rebalanced

  return promoteLargestValueRouteWhenNoDefault(next, allValueIds)
}

function rebalanceTraitDefaultVersusValueRoutes(next: TemplateRouting, allValueIds: string[]) {
  const valueRoutes = next.valueRoutes ?? []
  const defaultOptimized = next.default
  const usedByValueRoutes = new Set(valueRoutes.flatMap(vr => vr.fromIds))
  const defaultIds = allValueIds.filter(id => !usedByValueRoutes.has(id))

  interface Branch {
    ids: string[]
    subtree: TemplateRouting
  }

  const branches: Branch[] = []
  if (defaultOptimized && (defaultIds.length > 0 || valueRoutes.length === 0)) {
    branches.push({ ids: defaultIds, subtree: defaultOptimized })
  }
  for (const vr of valueRoutes) {
    branches.push({ ids: vr.fromIds, subtree: vr.to })
  }

  const positive = branches.filter(b => b.ids.length > 0)
  if (positive.length < 2) {
    return undefined
  }

  let bestIndex = 0
  for (let i = 1; i < positive.length; i++) {
    if (positive[i].ids.length > positive[bestIndex].ids.length) {
      bestIndex = i
    }
  }

  const winner = positive[bestIndex]
  const newValueRoutes = positive
    .filter((_, i) => i !== bestIndex)
    .map(b => ({ fromIds: b.ids, to: b.subtree }))

  return {
    ...next,
    default: winner.subtree,
    valueRoutes: newValueRoutes.length > 0 ? newValueRoutes : undefined,
  }
}

function promoteLargestValueRouteWhenNoDefault(
  next: TemplateRouting,
  allValueIds: string[]
): TemplateRouting {
  const valueRoutes = next.valueRoutes ?? []
  if (next.default || valueRoutes.length === 0) return next

  const covered = new Set(valueRoutes.flatMap(vr => vr.fromIds))
  if (!allValueIds.every(id => covered.has(id))) return next

  const bestIndex = valueRoutes.reduce(
    (best, vr, i, arr) => (vr.fromIds.length > arr[best].fromIds.length ? i : best),
    0
  )
  const remaining = [...valueRoutes]
  const [chosen] = remaining.splice(bestIndex, 1)
  return {
    ...next,
    default: chosen.to,
    valueRoutes: remaining.length > 0 ? remaining : undefined,
  }
}

/** Get all routes from a routing tree putting in best order */
export function getTemplateRouteNodes(routing: TemplateRouting): TemplateRouteNode[] {
  const routes: TemplateRouteNode[] = []

  function traverseRouting(node: TemplateRouting): void {
    const route = { key: node.key, type: node.type }
    if (!routes.some(r => r.key === route.key && r.type === route.type)) routes.push(route)

    if (node.default) traverseRouting(node.default)

    if (node.valueRoutes)
      node.valueRoutes.forEach(valueRoute => {
        traverseRouting(valueRoute.to)
      })
  }

  traverseRouting(routing)
  return routes
}

export function getNamedLayoutMap(layoutMap: Record<number, ILayout>): Record<number, ILayout> {
  return Object.fromEntries(Object.entries(layoutMap).filter(([_, layout]) => layout.label))
}

export function getTemplateRoutes(routing?: TemplateRouting): TemplateRoute[] {
  if (!routing) return []

  const routes: TemplateRoute[] = []

  function traverseRouting(node: TemplateRouting, currentRoute: TemplateRoute): void {
    const hasDefault = Boolean(node.default)
    const valueRoutes = node.valueRoutes
    const hasValueBranches = Boolean(valueRoutes?.length)

    if (hasDefault) {
      traverseRouting(node.default!, [...currentRoute, 'default'])
    }
    if (hasValueBranches) {
      valueRoutes!.forEach((valueRoute, index) => {
        traverseRouting(valueRoute.to, [...currentRoute, index])
      })
    }
    if (!hasDefault && !hasValueBranches) {
      routes.push([...currentRoute])
    }
  }

  traverseRouting(routing, [])
  return routes
}

/** Get all routings from a route */
export function getRoutings(routing?: TemplateRouting, route?: TemplateRoute): TemplateRouting[] {
  if (!routing || !route) return []

  const routes: TemplateRouting[] = [routing]
  let currentRouting = routing

  for (const step of route) {
    if (step === 'default') {
      currentRouting = currentRouting.default!
    } else {
      currentRouting = currentRouting.valueRoutes?.[step]?.to!
    }
    routes.push(currentRouting)
  }
  return routes
}

/** Get the final routing from a route */
export function getRouting(routing: TemplateRouting, route: TemplateRoute): TemplateRouting {
  const routes = getRoutings(routing, route)
  return routes[routes.length - 1]
}

export function removeChildFromRouting(
  routing: TemplateRouting,
  child: TemplateRouteNode,
  routeStep: TemplateRoute[number]
): TemplateRouting {
  const next: TemplateRouting = { ...routing }

  if (routeStep === 'default') {
    if (next.default?.key === child.key && next.default?.type === child.type) {
      delete next.default
    }
    return next
  }

  if (typeof routeStep === 'number') {
    const valueRoutes = [...(next.valueRoutes ?? [])]
    const valueRoute = valueRoutes[routeStep]

    if (
      valueRoute &&
      valueRoute.to.key === child.key &&
      valueRoute.to.type === child.type
    ) {
      valueRoutes.splice(routeStep, 1)
      if (valueRoutes.length > 0) next.valueRoutes = valueRoutes
      else delete next.valueRoutes
    }
  }

  return next
}

export function resolveRouteStepForChildWithGroup(
  parentRouting: TemplateRouting,
  child: TemplateRouteNode,
  groupIds: string[],
  trait?: ITrait
): number | 'default' | undefined {
  if (groupIds.length && trait && parentRouting.type === 'trait') {
    const valueRouteIndex =
      parentRouting.valueRoutes?.findIndex(
        valueRoute =>
          valueRoute.to.key === child.key &&
          valueRoute.to.type === child.type &&
          groupIds.every(id => valueRoute.fromIds.includes(id))
      ) ?? -1

    if (valueRouteIndex > -1) return valueRouteIndex

    if (
      parentRouting.default?.key === child.key &&
      parentRouting.default?.type === child.type &&
      groupIds.every(id => getDefaultValueIds(parentRouting, trait).includes(id))
    ) {
      return 'default'
    }
  }

  return resolveRouteStepForChild(parentRouting, child)
}

export function resolveRoutePath(
  routing: TemplateRouting,
  targetLength: number,
  previousRoute: TemplateRoute,
  previousRoutings: TemplateRouting[],
  traitMap: Record<number, ITrait>
): TemplateRoute {
  const resolved: TemplateRoute = []
  let currentRouting = routing

  for (let index = 0; index < targetLength; index++) {
    const childRouting = previousRoutings[index + 1]
    if (!childRouting) break

    const previousParentRouting = previousRoutings[index]
    const trait =
      previousParentRouting.type === 'trait' ? traitMap[previousParentRouting.key] : undefined
    const groupIds = trait
      ? getTargetGroupIdsFromRoute(
          previousParentRouting,
          trait,
          previousRoute[index],
          childRouting
        )
      : []
    const step = resolveRouteStepForChildWithGroup(
      currentRouting,
      { key: childRouting.key, type: childRouting.type },
      groupIds,
      currentRouting.type === 'trait' ? traitMap[currentRouting.key] : undefined
    )

    if (step === undefined) break

    resolved.push(step)
    currentRouting =
      step === 'default' ? currentRouting.default! : currentRouting.valueRoutes![step].to
  }

  return resolved
}

export function resolveRouteStepForChild(
  parentRouting: TemplateRouting,
  child: TemplateRouteNode
): number | 'default' | undefined {
  if (parentRouting.default?.key === child.key && parentRouting.default?.type === child.type) {
    return 'default'
  }

  const valueRouteIndex = parentRouting.valueRoutes?.findIndex(
    vr => vr.to.key === child.key && vr.to.type === child.type
  )
  if (valueRouteIndex != null && valueRouteIndex > -1) return valueRouteIndex

  return undefined
}
